import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  tripsApi,
  type AllocationBasis,
  type TripAllocationView,
} from "../../api/transport";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { canSign, draftTotal, offBy, proposalAsDraft, signedAsDraft, type Draft } from "./allocation";
import { formatNumber } from "../../lib/format";

const BASES: AllocationBasis[] = ["DECLARED_PRORATA", "THIRD_PARTY_TICKET", "MANUAL"];

/**
 * Splitting one weighbridge net across the several suppliers on one inbound lorry.
 *
 * <p>Rendered only for an inbound trip carrying more than one item: with a single
 * supplier the whole net is theirs and the delivery's own receipt already records
 * it. The screen exists because on a shared lorry those kilograms cannot be
 * measured per supplier — they are allocated, and somebody has to sign for that.
 *
 * <p>The sum must land on the net exactly. The button stays disabled until it does,
 * and the difference is shown in kilograms rather than as a red border, because
 * "3,40 kg too much" tells the operator what to change and a red border does not.
 */
export function AllocationSection({ tripId, canManage }: { tripId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();

  const [view, setView] = useState<TripAllocationView | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [basis, setBasis] = useState<AllocationBasis>("DECLARED_PRORATA");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void tripsApi
      .allocation(tripId)
      .then((v) => {
        if (cancelled) return;
        setView(v);
        setReason(v.massVarianceReason ?? "");
        // Reopen on the standing split; fall back to the server's proposal, which
        // is itself empty when any supplier declared nothing.
        const signed = signedAsDraft(v.items);
        setDraft(Object.keys(signed).length > 0 ? signed : proposalAsDraft(v.items));
        const already = v.items.find((i) => i.allocationBasis != null)?.allocationBasis;
        if (already) setBasis(already);
      })
      // A caller outside the otkup assignment for any supplier on this lorry is
      // refused, and that is deliberate — the proposal already names them all.
      .catch(() => {
        if (!cancelled) setView(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  if (!view) return null;

  const { kg: total, complete } = draftTotal(view.items, draft);
  const difference = offBy(view.netKg, total);
  const signable = canManage && canSign(view.netKnown, view.netKg, view.items, draft);
  const anyStale = view.items.some((i) => i.stale);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!view) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await tripsApi.allocate(tripId, {
        basis,
        items: view.items.map((i) => ({
          itemId: i.itemId,
          allocatedKg: Number(draft[i.itemId]),
        })),
        massVarianceReason: reason.trim() === "" ? undefined : reason.trim(),
      });
      setView(updated);
      toast.success(t("trips.allocation.toast.signed", "Allocation signed."));
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={card}>
      <h2 style={cardTitle}>{t("trips.allocation.title", "Mass allocation")}</h2>
      <p style={hint}>
        {t(
          "trips.allocation.hint",
          "The weighbridge weighs the lorry, not the supplier. On a shared load each share is allocated, not measured — and the settlement note says so.",
        )}
      </p>

      {!view.netKnown ? (
        // Never a blank that reads as zero.
        <p style={warn}>
          {t("trips.allocation.notWeighed", "This trip has no gross/tare pair yet, so there is no net to divide.")}
        </p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10, fontSize: 13 }}>
            <span>
              <strong>{t("trips.allocation.net", "Net")}:</strong> {fmt(view.netKg)} kg
            </span>
            {view.declaredTotalKg != null && (
              <span style={{ color: "var(--ink-2)" }}>
                {t("trips.allocation.declared", "Declared")}: {fmt(view.declaredTotalKg)} kg
                {view.variancePct != null && ` (${view.variancePct > 0 ? "+" : ""}${view.variancePct}%)`}
              </span>
            )}
          </div>

          {anyStale && (
            <p style={warn}>
              {t(
                "trips.allocation.stale",
                "The lorry was weighed again after this split was signed. Settlement is blocked until it is signed anew.",
              )}
            </p>
          )}

          <form onSubmit={submit}>
            <table className="list-card">
              <thead>
                <tr>
                  <th style={th}>{t("trips.col.counterparty", "Counterparty")}</th>
                  <th style={th}>{t("trips.allocation.declaredKg", "Declared (kg)")}</th>
                  <th style={th}>{t("trips.allocation.proposedKg", "Proposed (kg)")}</th>
                  <th style={th}>{t("trips.allocation.allocatedKg", "Allocated (kg)")}</th>
                </tr>
              </thead>
              <tbody>
                {view.items.map((i) => (
                  <tr key={i.itemId}>
                    <td style={td} data-label={t("trips.col.counterparty", "Counterparty")}>
                      {i.supplierName ?? "—"}
                    </td>
                    <td style={td} data-label={t("trips.allocation.declaredKg", "Declared (kg)")}>
                      {fmt(i.declaredKg)}
                    </td>
                    <td style={td} data-label={t("trips.allocation.proposedKg", "Proposed (kg)")}>
                      {i.proposedKg == null ? "—" : fmt(i.proposedKg)}
                    </td>
                    <td style={td} data-label={t("trips.allocation.allocatedKg", "Allocated (kg)")}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="input"
                        style={{ width: 140 }}
                        value={draft[i.itemId] ?? ""}
                        disabled={!canManage || busy}
                        onChange={(e) => setDraft((d) => ({ ...d, [i.itemId]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ margin: "8px 0", fontSize: 13 }}>
              {t("trips.allocation.total", "Allocated total")}: <strong>{fmt(total)} kg</strong>
              {!complete && (
                <span style={{ color: "var(--ink-3)" }}>
                  {" · "}
                  {t("trips.allocation.incomplete", "some rows have no figure yet")}
                </span>
              )}
              {complete && difference !== 0 && difference != null && (
                // The number, not a red border: it says what to change.
                <span style={{ color: "var(--err)" }}>
                  {" · "}
                  {difference > 0
                    ? t("trips.allocation.over", "{{kg}} kg too much", { kg: fmt(difference) })
                    : t("trips.allocation.under", "{{kg}} kg short", { kg: fmt(Math.abs(difference)) })}
                </span>
              )}
            </p>

            {canManage && (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 220 }}>
                    <span style={fieldLabel}>{t("trips.allocation.basis", "Basis")}</span>
                    <Select
                      value={basis}
                      onChange={(v) => setBasis(v as AllocationBasis)}
                      options={BASES.map((b) => ({
                        value: b,
                        label: t(`trips.allocation.basis.${b.toLowerCase()}`),
                      }))}
                    />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 240 }}>
                    <span style={fieldLabel}>
                      {t("trips.allocation.varianceReason", "Reason for the difference from declared")}
                    </span>
                    <input
                      type="text"
                      className="input"
                      value={reason}
                      disabled={busy}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </label>
                </div>

                {err && <div style={errBox}>{err}</div>}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                  <button type="submit" className="btn primary" disabled={!signable || busy}>
                    {busy ? "…" : t("trips.allocation.sign", "Sign allocation")}
                  </button>
                </div>
              </>
            )}
          </form>
        </>
      )}
    </section>
  );
}

function fmt(n: number | null): string {
  return formatNumber(n, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 15, fontWeight: 600 };
const hint: CSSProperties = { margin: "0 0 8px", fontSize: 12, color: "var(--ink-3)" };
const warn: CSSProperties = {
  margin: "0 0 8px",
  fontSize: 13,
  color: "var(--ink-2)",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "8px 10px",
};
const th: CSSProperties = { textAlign: "left", fontSize: 12, color: "var(--ink-3)", fontWeight: 600 };
const td: CSSProperties = { fontSize: 13 };
const fieldLabel: CSSProperties = { fontSize: 12, color: "var(--ink-3)" };
const errBox: CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: "var(--err)",
  background: "var(--surface-2)",
  borderRadius: 8,
  padding: "8px 10px",
};
